import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, FloppyDisk, Plus, Trash, Image as ImageIcon,
  Eye, EyeSlash, Package, Tag, ShieldCheck, Users,
  Scales, Leaf, Fire, CircleNotch, UploadSimple, X, GridNine, Copy
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import RichTextEditor from '../../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UNITS = [
  { value: 'piece', label: 'Pièce' },
  { value: 'kg', label: 'Kilogramme (kg)' },
  { value: 'g', label: 'Gramme (g)' },
  { value: 'litre', label: 'Litre (L)' },
  { value: 'ml', label: 'Millilitre (mL)' },
  { value: 'kit', label: 'Kit' },
  { value: 'lot', label: 'Lot' },
  { value: 'metre', label: 'Mètre (m)' },
  { value: 'cm', label: 'Centimètre (cm)' },
];

const defaultProduct = {
  name: '', slug: '', description: '', short_description: '',
  product_type: 'simple',
  price: 0, sale_price: null, cost_price: null, sku: '',
  stock_quantity: 0, manage_stock: true, stock_status: 'instock',
  category_ids: [], featured_image: null, images: [], image_protection: false,
  attributes: [], variations: [], weight: 0.4, dimensions: null,
  meta_title: null, meta_description: null,
  unit_of_measure: null, unit_price: null, unit_label: null,
  brand: null, origin: null,
  is_food_product: false, price_per_kg: null, allergens: null,
  composition: null, nutritional_info: null,
  visitor_counter_enabled: false, visitor_counter_type: 'visitors',
  visitor_counter_min: 5, visitor_counter_max: 25, visitor_counter_label: null,
  upsell_product_ids: [], crosssell_product_ids: [],
  is_bundle: false, bundle_products: [], bundle_discount_percent: null,
  price_pro: null, volume_discounts: [],
  is_promo: false, promo_end_date: null, promo_label: null, promo_quantity_limit: null,
  is_featured: false, is_seasonal: false,
};

/* ── Image Upload Component ── */
const ImageUploader = ({ onUploaded, multiple = false, label = "Ajouter" }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      if (multiple) {
        const fd = new FormData();
        Array.from(files).forEach(f => fd.append('files', f));
        const { data } = await axios.post(`${API_URL}/api/upload/multiple`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        onUploaded(data.urls);
      } else {
        const fd = new FormData();
        fd.append('file', files[0]);
        const { data } = await axios.post(`${API_URL}/api/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        onUploaded(data.url);
      }
    } catch { toast.error("Erreur d'upload"); }
    finally { setUploading(false); }
  };

  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={e => handleFiles(e.target.files)} />
      <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={uploading} data-testid="upload-image-btn">
        {uploading ? <CircleNotch className="animate-spin mr-1" size={14} /> : <UploadSimple className="mr-1" size={14} />}
        {uploading ? 'Upload...' : label}
      </Button>
    </>
  );
};

const ProductFormPage = () => {
  const { productId } = useParams();
  const { currentStore } = useStore();
  const navigate = useNavigate();
  const isEdit = productId && productId !== 'new';
  const [product, setProduct] = useState(defaultProduct);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [newAttr, setNewAttr] = useState({ name: '', values: '', visible: true, used_for_variations: false });
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  const fetchProduct = useCallback(async () => {
    if (!isEdit || !currentStore) return;
    try {
      const resp = await axios.get(`${API_URL}/api/stores/${currentStore.id}/products`, { withCredentials: true });
      const found = resp.data.products?.find(p => p.id === productId);
      if (found) setProduct({ ...defaultProduct, ...found });
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [isEdit, currentStore, productId]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  useEffect(() => {
    if (!currentStore) return;
    axios.get(`${API_URL}/api/stores/${currentStore.id}/products`, { withCredentials: true })
      .then(r => setAllProducts(r.data.products || []))
      .catch(() => {});
  }, [currentStore]);

  const fetchCategories = useCallback(async () => {
    if (!currentStore) return;
    try {
      const r = await axios.get(`${API_URL}/api/stores/${currentStore.id}/categories`, { withCredentials: true });
      setCategories(r.data || []);
    } catch { /* ignore */ }
  }, [currentStore]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const toggleCategory = (catId) => {
    const ids = product.category_ids || [];
    update('category_ids', ids.includes(catId) ? ids.filter(i => i !== catId) : [...ids, catId]);
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name || !currentStore) return;
    setCreatingCategory(true);
    try {
      const slug = autoSlug(name);
      const r = await axios.post(`${API_URL}/api/stores/${currentStore.id}/categories`,
        { name, slug, parent_id: null, image_url: null },
        { withCredentials: true }
      );
      const newId = r.data.id;
      await fetchCategories();
      if (newId) update('category_ids', [...(product.category_ids || []), newId]);
      setNewCategoryName('');
      toast.success('Catégorie créée');
    } catch { toast.error("Erreur création catégorie"); }
    finally { setCreatingCategory(false); }
  };

  const update = (field, value) => setProduct(p => ({ ...p, [field]: value }));
  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSave = async () => {
    if (!currentStore) { toast.error('Aucune boutique sélectionnée'); return; }
    if (!product.name?.trim()) { toast.error('Le nom du produit est obligatoire'); return; }
    if (!product.price || product.price <= 0) { toast.error('Le prix doit être supérieur à 0'); return; }
    if (product.sale_price && product.sale_price >= product.price) {
      toast.error('Le prix promo doit être inférieur au prix de vente');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...product };
      if (!payload.slug) payload.slug = autoSlug(payload.name);
      delete payload.id;
      delete payload.store_id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.margin;
      delete payload.category;
      delete payload.wc_id;

      if (isEdit) {
        await axios.put(`${API_URL}/api/stores/${currentStore.id}/products/${productId}`, payload, { withCredentials: true });
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API_URL}/api/stores/${currentStore.id}/products`, payload, { withCredentials: true });
        toast.success('Produit créé');
        navigate('/admin/products');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Erreur lors de la sauvegarde');
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const addAttribute = () => {
    if (!newAttr.name) return;
    const vals = newAttr.values.split(',').map(v => v.trim()).filter(Boolean);
    update('attributes', [...product.attributes, { name: newAttr.name, values: vals, visible: newAttr.visible, used_for_variations: newAttr.used_for_variations }]);
    setNewAttr({ name: '', values: '', visible: true, used_for_variations: false });
  };

  const removeAttribute = (idx) => {
    update('attributes', product.attributes.filter((_, i) => i !== idx));
  };

  const toggleAttrVariation = (idx, val) => {
    const updated = [...product.attributes];
    updated[idx] = { ...updated[idx], used_for_variations: val };
    update('attributes', updated);
  };

  /* ── Generate variations from attributes ── */
  const generateVariations = () => {
    const varAttrs = product.attributes.filter(a => a.used_for_variations && a.values?.length);
    if (!varAttrs.length) { toast.error("Cochez 'Utilisé pour les variations' sur au moins un attribut"); return; }

    const combos = varAttrs.reduce((acc, attr) => {
      if (!acc.length) return attr.values.map(v => ({ [attr.name]: v }));
      return acc.flatMap(combo => attr.values.map(v => ({ ...combo, [attr.name]: v })));
    }, []);

    const existing = product.variations || [];
    const newVars = combos.map(combo => {
      const match = existing.find(v => v.attributes && JSON.stringify(v.attributes) === JSON.stringify(combo));
      return match || {
        attributes: combo,
        price: product.price || 0,
        sale_price: null,
        stock_quantity: 0,
        sku: '',
        image: null,
        enabled: true,
      };
    });
    update('variations', newVars);
    toast.success(`${newVars.length} variation(s) générée(s)`);
  };

  const updateVariation = (idx, field, value) => {
    const vars = [...product.variations];
    vars[idx] = { ...vars[idx], [field]: value };
    update('variations', vars);
  };

  const removeVariation = (idx) => {
    update('variations', product.variations.filter((_, i) => i !== idx));
  };

  /* ── Bulk apply price to all variations ── */
  const bulkApplyPrice = () => {
    const vars = product.variations.map(v => ({ ...v, price: product.price }));
    update('variations', vars);
    toast.success('Prix appliqué à toutes les variations');
  };

  if (loading) return <div className="flex justify-center py-12"><CircleNotch className="animate-spin" size={32} /></div>;

  const margin = product.price && product.cost_price
    ? Math.round(((product.price - product.cost_price) / product.price) * 100)
    : null;

  const isVariable = product.product_type === 'variable';

  return (
    <div className="space-y-6" data-testid="product-form-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/products')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{isEdit ? 'Modifier le produit' : 'Nouveau produit'}</h1>
            <p className="text-sm text-slate-500">{currentStore?.name}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-product-btn">
          {saving ? <CircleNotch className="animate-spin mr-2" size={16} /> : <FloppyDisk className="mr-2" size={16} />}
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg mb-4 flex-wrap h-auto gap-1">
          <TabsTrigger value="general" className="text-xs data-[state=active]:bg-white"><Package className="mr-1" size={14} />Général</TabsTrigger>
          <TabsTrigger value="images" className="text-xs data-[state=active]:bg-white"><ImageIcon className="mr-1" size={14} />Images</TabsTrigger>
          <TabsTrigger value="attributes" className="text-xs data-[state=active]:bg-white"><Tag className="mr-1" size={14} />Attributs</TabsTrigger>
          {isVariable && <TabsTrigger value="variations" className="text-xs data-[state=active]:bg-white"><GridNine className="mr-1" size={14} />Variations</TabsTrigger>}
          <TabsTrigger value="food" className="text-xs data-[state=active]:bg-white"><Leaf className="mr-1" size={14} />Alimentaire</TabsTrigger>
          <TabsTrigger value="social" className="text-xs data-[state=active]:bg-white"><Fire className="mr-1" size={14} />Social proof</TabsTrigger>
          <TabsTrigger value="upsell" className="text-xs data-[state=active]:bg-white"><Users className="mr-1" size={14} />Ventes croisées</TabsTrigger>
          <TabsTrigger value="seo" className="text-xs data-[state=active]:bg-white"><ShieldCheck className="mr-1" size={14} />SEO</TabsTrigger>
        </TabsList>

        {/* GENERAL TAB */}
        <TabsContent value="general">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-xl border p-5 space-y-4">
                <h3 className="font-semibold text-slate-900">Informations</h3>
                {/* Product type selector */}
                <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <Label className="text-sm font-medium text-blue-800">Type de produit</Label>
                  <Select value={product.product_type || 'simple'} onValueChange={v => update('product_type', v)}>
                    <SelectTrigger className="w-48 bg-white" data-testid="product-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simple">Produit simple</SelectItem>
                      <SelectItem value="variable">Produit variable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du produit *</Label>
                    <Input value={product.name} onChange={e => { update('name', e.target.value); if (!isEdit) update('slug', autoSlug(e.target.value)); }} data-testid="product-name" />
                  </div>
                  <div>
                    <Label>Slug</Label>
                    <Input value={product.slug} onChange={e => update('slug', e.target.value)} data-testid="product-slug" />
                  </div>
                </div>
                <div>
                  <Label>Description courte</Label>
                  <Input value={product.short_description || ''} onChange={e => update('short_description', e.target.value)} data-testid="product-short-desc" />
                </div>
                <div>
                  <Label>Description détaillée</Label>
                  <RichTextEditor
                    content={product.description || ''}
                    onChange={val => update('description', val)}
                    placeholder="Décrivez votre produit en détail..."
                    minHeight={200}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Marque</Label>
                    <Input value={product.brand || ''} onChange={e => update('brand', e.target.value)} placeholder="Ex: Nike, Magère" />
                  </div>
                  <div>
                    <Label>Origine</Label>
                    <Input value={product.origin || ''} onChange={e => update('origin', e.target.value)} placeholder="Ex: France, Italie" />
                  </div>
                </div>
              </div>

              {/* Pricing - shown for simple products or as base price for variable */}
              <div className="bg-white rounded-xl border p-5 space-y-4">
                <h3 className="font-semibold text-slate-900">
                  {isVariable ? 'Tarification de base' : 'Tarification'}
                </h3>
                {isVariable && <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">Pour un produit variable, chaque variation peut avoir son propre prix. Le prix ci-dessous sert de base.</p>}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Prix de vente (EUR) *</Label>
                    <Input type="number" step="0.01" value={product.price} onChange={e => update('price', parseFloat(e.target.value) || 0)} data-testid="product-price" />
                  </div>
                  <div>
                    <Label>Prix promo (EUR)</Label>
                    <Input type="number" step="0.01" value={product.sale_price || ''} onChange={e => update('sale_price', e.target.value ? parseFloat(e.target.value) : null)} />
                  </div>
                  <div>
                    <Label>Prix d'achat (EUR)</Label>
                    <Input type="number" step="0.01" value={product.cost_price || ''} onChange={e => update('cost_price', e.target.value ? parseFloat(e.target.value) : null)} />
                  </div>
                  <div>
                    <Label>Marge</Label>
                    <div className={`h-9 flex items-center px-3 rounded-md border text-sm font-semibold ${margin > 30 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : margin > 15 ? 'bg-amber-50 text-amber-700 border-amber-200' : margin !== null ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-50 text-slate-400'}`}>
                      {margin !== null ? `${margin}%` : '-'}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2 border-t">
                  <div>
                    <Label>Unité de mesure</Label>
                    <Select value={product.unit_of_measure || ''} onValueChange={v => update('unit_of_measure', v)}>
                      <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Prix à l'unité</Label>
                    <Input type="number" step="0.01" value={product.unit_price || ''} onChange={e => update('unit_price', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Ex: 25.00" />
                  </div>
                  <div>
                    <Label>Libellé unité</Label>
                    <Input value={product.unit_label || ''} onChange={e => update('unit_label', e.target.value)} placeholder="Ex: le kg, la pièce" />
                  </div>
                </div>

                {/* B2B / Pro Pricing */}
                <div className="pt-3 border-t space-y-3">
                  <h4 className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-purple-100 flex items-center justify-center text-[10px] font-bold">P</span>
                    Tarification Pro (B2B)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Prix professionnel (EUR)</Label>
                      <Input type="number" step="0.01" value={product.price_pro || ''} onChange={e => update('price_pro', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Laisser vide = prix standard" data-testid="product-price-pro" />
                      <p className="text-[11px] text-slate-400 mt-1">Affiche automatiquement aux clients Pro</p>
                    </div>
                  </div>

                  {/* Volume Discounts */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs">Remises sur volume</Label>
                      <button type="button" className="text-[11px] font-semibold text-purple-600 hover:underline"
                        onClick={() => update('volume_discounts', [...(product.volume_discounts || []), { min_qty: 5, price: product.price || 0 }])}
                        data-testid="add-volume-discount">
                        + Ajouter palier
                      </button>
                    </div>
                    {(product.volume_discounts || []).map((vd, i) => (
                      <div key={i} className="flex items-center gap-2 mb-2" data-testid={`volume-discount-${i}`}>
                        <div className="flex-1 flex items-center gap-2">
                          <span className="text-xs text-slate-500 whitespace-nowrap">Des</span>
                          <Input type="number" className="h-8 text-sm w-20" value={vd.min_qty || ''} onChange={e => {
                            const vds = [...(product.volume_discounts || [])];
                            vds[i] = { ...vds[i], min_qty: parseInt(e.target.value) || 0 };
                            update('volume_discounts', vds);
                          }} />
                          <span className="text-xs text-slate-500">pcs a</span>
                          <Input type="number" step="0.01" className="h-8 text-sm w-24" value={vd.price || ''} onChange={e => {
                            const vds = [...(product.volume_discounts || [])];
                            vds[i] = { ...vds[i], price: parseFloat(e.target.value) || 0 };
                            update('volume_discounts', vds);
                          }} />
                          <span className="text-xs text-slate-500">EUR/u</span>
                        </div>
                        <button type="button" className="text-red-400 hover:text-red-600 text-sm" onClick={() => {
                          const vds = [...(product.volume_discounts || [])];
                          vds.splice(i, 1);
                          update('volume_discounts', vds);
                        }}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              {/* Categories */}
              <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="category-card">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Tag size={16} style={{ color: '#0052FF' }} /> Catégories
                </h3>
                {categories.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucune catégorie — créez-en une ci-dessous.</p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {categories.map(cat => {
                      const checked = (product.category_ids || []).includes(cat.id);
                      return (
                        <label key={cat.id} className={`flex items-center gap-2 p-1.5 rounded-md cursor-pointer transition-colors ${checked ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCategory(cat.id)}
                            className="accent-[#0052FF] w-4 h-4"
                            data-testid={`category-checkbox-${cat.id}`}
                          />
                          <span className="text-sm text-slate-700">{cat.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="pt-2 border-t flex gap-2">
                  <Input
                    placeholder="Nouvelle catégorie"
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleCreateCategory())}
                    className="text-sm h-9"
                    data-testid="new-category-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleCreateCategory}
                    disabled={creatingCategory || !newCategoryName.trim()}
                    className="bg-[#0052FF] hover:bg-[#0040CC] h-9"
                    data-testid="create-category-btn"
                  >
                    {creatingCategory ? <CircleNotch className="animate-spin" size={14} /> : <Plus size={14} />}
                  </Button>
                </div>
              </div>

              {/* Promotion */}
              <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="promo-card">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Fire size={16} className="text-red-500" /> Promotion
                  </h3>
                  <Switch
                    checked={!!product.is_promo}
                    onCheckedChange={v => {
                      update('is_promo', v);
                      if (!v) { update('promo_end_date', null); update('promo_label', null); update('promo_quantity_limit', null); }
                    }}
                    data-testid="promo-enabled-switch"
                  />
                </div>
                {product.is_promo && (
                  <div className="space-y-3 pt-1">
                    {!product.sale_price && (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                        Astuce : renseignez un <strong>Prix promo</strong> dans la section Tarification pour activer la remise.
                      </p>
                    )}
                    <div>
                      <Label className="text-xs">Libellé de promo</Label>
                      <Input
                        value={product.promo_label || ''}
                        onChange={e => update('promo_label', e.target.value || null)}
                        placeholder="Ex: -20%, OFFRE FÊTES, FLASH"
                        data-testid="promo-label-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Date de fin de promo</Label>
                      <Input
                        type="date"
                        value={product.promo_end_date ? product.promo_end_date.slice(0, 10) : ''}
                        onChange={e => update('promo_end_date', e.target.value || null)}
                        data-testid="promo-end-date-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Limite de quantité (optionnel)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={product.promo_quantity_limit || ''}
                        onChange={e => update('promo_quantity_limit', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="Ex: 50 unités"
                        data-testid="promo-quantity-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border p-5 space-y-4">
                <h3 className="font-semibold text-slate-900">Stock</h3>
                <div>
                  <Label>SKU</Label>
                  <Input value={product.sku || ''} onChange={e => update('sku', e.target.value)} />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Gérer le stock</Label>
                  <Switch checked={product.manage_stock} onCheckedChange={v => update('manage_stock', v)} />
                </div>
                {product.manage_stock && (
                  <div>
                    <Label>Quantité en stock</Label>
                    <Input type="number" value={product.stock_quantity} onChange={e => update('stock_quantity', parseInt(e.target.value) || 0)} />
                  </div>
                )}
                <div>
                  <Label>Statut</Label>
                  <Select value={product.stock_status} onValueChange={v => update('stock_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instock">En stock</SelectItem>
                      <SelectItem value="outofstock">Rupture de stock</SelectItem>
                      <SelectItem value="onbackorder">Sur commande</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="bg-white rounded-xl border p-5 space-y-4">
                <h3 className="font-semibold text-slate-900">Expédition</h3>
                <div>
                  <Label>Poids (kg)</Label>
                  <Input type="number" step="0.01" value={product.weight || ''} onChange={e => update('weight', e.target.value ? parseFloat(e.target.value) : null)} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Long (cm)</Label>
                    <Input type="number" value={product.dimensions?.length || ''} onChange={e => update('dimensions', { ...product.dimensions, length: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Larg (cm)</Label>
                    <Input type="number" value={product.dimensions?.width || ''} onChange={e => update('dimensions', { ...product.dimensions, width: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Haut (cm)</Label>
                    <Input type="number" value={product.dimensions?.height || ''} onChange={e => update('dimensions', { ...product.dimensions, height: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* IMAGES TAB */}
        <TabsContent value="images">
          <div className="bg-white rounded-xl border p-5 space-y-5">
            <h3 className="font-semibold text-slate-900">Image de mise en avant</h3>
            <div className="flex items-start gap-4">
              <div className="w-40 h-40 border-2 border-dashed rounded-xl overflow-hidden flex items-center justify-center bg-slate-50 relative group">
                {product.featured_image ? (
                  <>
                    <img src={product.featured_image.startsWith('/') ? API_URL + product.featured_image : product.featured_image} alt="Featured" className="w-full h-full object-cover" />
                    <button onClick={() => update('featured_image', null)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-featured-image">
                      <X size={12} />
                    </button>
                  </>
                ) : (
                  <ImageIcon size={40} className="text-slate-300" />
                )}
              </div>
              <div className="space-y-2">
                <ImageUploader label="Uploader une image" onUploaded={url => update('featured_image', url)} />
                <p className="text-xs text-slate-500">Cette image sera affichée en premier sur la fiche produit.</p>
                <Input value={product.featured_image || ''} onChange={e => update('featured_image', e.target.value)} placeholder="ou coller une URL" className="text-xs" data-testid="featured-image-input" />
              </div>
            </div>

            <div className="border-t pt-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Galerie d'images ({product.images.length})</h3>
                <ImageUploader label="Ajouter des images" multiple onUploaded={urls => update('images', [...product.images, ...urls])} />
              </div>
              {product.images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {product.images.map((img, i) => (
                    <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border bg-slate-50">
                      <img src={img.startsWith('/') ? API_URL + img : img} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => update('images', product.images.filter((_, j) => j !== i))}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-gallery-image-${i}`}>
                        <Trash size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">Aucune image dans la galerie</p>
              )}
            </div>

            <div className="border-t pt-5 flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Protection des images</Label>
                <p className="text-xs text-slate-500">Désactive le clic droit et l'enregistrement des images</p>
              </div>
              <Switch checked={product.image_protection} onCheckedChange={v => update('image_protection', v)} data-testid="image-protection-switch" />
            </div>
          </div>
        </TabsContent>

        {/* ATTRIBUTES TAB */}
        <TabsContent value="attributes">
          <div className="bg-white rounded-xl border p-5 space-y-5">
            <h3 className="font-semibold text-slate-900">Attributs du produit</h3>
            <p className="text-sm text-slate-500">
              Ajoutez des caractéristiques comme Taille, Couleur, Poids.
              {isVariable && <span className="font-medium text-blue-600"> Cochez "Variation" pour utiliser cet attribut dans les variations.</span>}
            </p>

            {product.attributes.map((attr, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`attribute-row-${i}`}>
                <div className="flex-1">
                  <p className="font-medium text-sm">{attr.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {attr.values?.map((v, vi) => (
                      <span key={vi} className="inline-block px-2 py-0.5 bg-white border rounded text-xs">{v}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs flex-shrink-0">
                  {attr.visible !== false ? (
                    <span className="flex items-center gap-1 text-emerald-600"><Eye size={14} /> Visible</span>
                  ) : (
                    <span className="flex items-center gap-1 text-slate-400"><EyeSlash size={14} /> Masqué</span>
                  )}
                  {isVariable && (
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={attr.used_for_variations || false}
                        onChange={e => toggleAttrVariation(i, e.target.checked)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-blue-600 font-medium">Variation</span>
                    </label>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeAttribute(i)}>
                  <Trash size={16} className="text-red-500" />
                </Button>
              </div>
            ))}

            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium">Ajouter un attribut</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input value={newAttr.name} onChange={e => setNewAttr(p => ({ ...p, name: e.target.value }))} placeholder="Nom (ex: Taille, Poids)" />
                <Input value={newAttr.values} onChange={e => setNewAttr(p => ({ ...p, values: e.target.value }))} placeholder="Valeurs séparées par virgule (ex: 500g, 1kg)" />
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={newAttr.visible} onCheckedChange={v => setNewAttr(p => ({ ...p, visible: v }))} />
                    <Label className="text-xs">Visible</Label>
                  </div>
                  {isVariable && (
                    <div className="flex items-center gap-2">
                      <Switch checked={newAttr.used_for_variations} onCheckedChange={v => setNewAttr(p => ({ ...p, used_for_variations: v }))} />
                      <Label className="text-xs">Variation</Label>
                    </div>
                  )}
                  <Button onClick={addAttribute} size="sm" className="bg-[#0052FF]" data-testid="add-attr-btn">
                    <Plus size={14} className="mr-1" /> Ajouter
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* VARIATIONS TAB (only for variable products) */}
        {isVariable && (
          <TabsContent value="variations">
            <div className="bg-white rounded-xl border p-5 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Variations ({product.variations.length})</h3>
                  <p className="text-sm text-slate-500">Chaque combinaison d'attributs crée une variation avec son propre prix, stock et image.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={bulkApplyPrice} disabled={!product.variations.length} data-testid="bulk-price-btn">
                    <Copy size={14} className="mr-1" /> Appliquer le prix de base
                  </Button>
                  <Button size="sm" className="bg-[#0052FF]" onClick={generateVariations} data-testid="generate-variations-btn">
                    <GridNine size={14} className="mr-1" /> Générer les variations
                  </Button>
                </div>
              </div>

              {!product.variations.length && (
                <div className="text-center py-10 text-slate-400">
                  <GridNine size={48} className="mx-auto mb-3" />
                  <p className="text-sm">Aucune variation. Ajoutez des attributs avec l'option "Variation" cochée, puis cliquez sur "Générer".</p>
                </div>
              )}

              {product.variations.map((v, i) => (
                <div key={i} className={`border rounded-lg p-4 space-y-3 ${v.enabled === false ? 'opacity-50 bg-slate-50' : 'bg-white'}`} data-testid={`variation-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      {Object.entries(v.attributes || {}).map(([k, val]) => (
                        <span key={k} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                          {k}: <strong>{val}</strong>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={v.enabled !== false} onCheckedChange={val => updateVariation(i, 'enabled', val)} />
                      <Button variant="ghost" size="icon" onClick={() => removeVariation(i)} data-testid={`remove-variation-${i}`}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <Label className="text-xs">Prix (EUR)</Label>
                      <Input type="number" step="0.01" value={v.price || ''} onChange={e => updateVariation(i, 'price', parseFloat(e.target.value) || 0)} data-testid={`variation-price-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Prix promo</Label>
                      <Input type="number" step="0.01" value={v.sale_price || ''} onChange={e => updateVariation(i, 'sale_price', e.target.value ? parseFloat(e.target.value) : null)} />
                    </div>
                    <div>
                      <Label className="text-xs">Stock</Label>
                      <Input type="number" value={v.stock_quantity || 0} onChange={e => updateVariation(i, 'stock_quantity', parseInt(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">SKU</Label>
                      <Input value={v.sku || ''} onChange={e => updateVariation(i, 'sku', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Image</Label>
                      <div className="flex items-center gap-2">
                        {v.image ? (
                          <div className="w-9 h-9 rounded border overflow-hidden flex-shrink-0 relative group">
                            <img src={v.image.startsWith('/') ? API_URL + v.image : v.image} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => updateVariation(i, 'image', null)}
                              className="absolute inset-0 bg-red-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <X size={10} className="text-white" />
                            </button>
                          </div>
                        ) : null}
                        <ImageUploader label="Image" onUploaded={url => updateVariation(i, 'image', url)} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        {/* FOOD TAB */}
        <TabsContent value="food">
          <div className="bg-white rounded-xl border p-5 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Fiche produit alimentaire</h3>
                <p className="text-sm text-slate-500">Informations obligatoires pour les produits alimentaires</p>
              </div>
              <Switch checked={product.is_food_product} onCheckedChange={v => update('is_food_product', v)} data-testid="food-product-switch" />
            </div>

            {product.is_food_product && (
              <div className="space-y-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Prix au kilo (EUR)</Label>
                    <Input type="number" step="0.01" value={product.price_per_kg || ''} onChange={e => update('price_per_kg', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Ex: 45.90" />
                  </div>
                  <div>
                    <Label>Poids net</Label>
                    <Input value={product.weight || ''} onChange={e => update('weight', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Ex: 0.4 kg (par défaut pour une paire de lunettes)" />
                  </div>
                </div>
                <div>
                  <Label>Composition / Ingrédients</Label>
                  <textarea className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500" value={product.composition || ''} onChange={e => update('composition', e.target.value)} placeholder="Liste des ingrédients..." />
                </div>
                <div>
                  <Label>Allergènes</Label>
                  <textarea className="w-full border rounded-lg p-3 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500" value={product.allergens || ''} onChange={e => update('allergens', e.target.value)} placeholder="Contient: gluten, lait, oeufs..." />
                </div>
                <div>
                  <Label>Informations nutritionnelles (par 100g)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                    {['calories', 'proteines', 'glucides', 'lipides', 'fibres', 'sel', 'sucres', 'acides_gras_satures'].map(key => (
                      <div key={key}>
                        <Label className="text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
                        <Input type="number" step="0.1" value={product.nutritional_info?.[key] || ''} onChange={e => update('nutritional_info', { ...product.nutritional_info, [key]: e.target.value ? parseFloat(e.target.value) : null })} placeholder="0" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* SOCIAL PROOF TAB */}
        <TabsContent value="social">
          <div className="bg-white rounded-xl border p-5 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Social proof / Urgence</h3>
                <p className="text-sm text-slate-500">Affiche un compteur de visiteurs ou d'achats pour inciter à l'achat</p>
              </div>
              <Switch checked={product.visitor_counter_enabled} onCheckedChange={v => update('visitor_counter_enabled', v)} data-testid="visitor-counter-switch" />
            </div>

            {product.visitor_counter_enabled && (
              <div className="space-y-4 pt-4 border-t">
                <div>
                  <Label>Type de compteur</Label>
                  <Select value={product.visitor_counter_type} onValueChange={v => update('visitor_counter_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visitors">Visiteurs en ce moment</SelectItem>
                      <SelectItem value="purchases">Achats dans l'heure</SelectItem>
                      <SelectItem value="views">Vues aujourd'hui</SelectItem>
                      <SelectItem value="cart">Ajouts au panier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre minimum</Label>
                    <Input type="number" value={product.visitor_counter_min} onChange={e => update('visitor_counter_min', parseInt(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label>Nombre maximum</Label>
                    <Input type="number" value={product.visitor_counter_max} onChange={e => update('visitor_counter_max', parseInt(e.target.value) || 0)} />
                  </div>
                </div>
                <div>
                  <Label>Texte personnalisé (optionnel)</Label>
                  <Input value={product.visitor_counter_label || ''} onChange={e => update('visitor_counter_label', e.target.value)} placeholder="Ex: {count} personnes regardent ce produit" />
                  <p className="text-xs text-slate-500 mt-1">Utilisez {'{count}'} pour afficher le nombre</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <Fire size={16} className="inline mr-1" weight="fill" />
                    Apercu: <strong>{Math.floor(Math.random() * (product.visitor_counter_max - product.visitor_counter_min) + product.visitor_counter_min)}</strong> {
                      product.visitor_counter_type === 'visitors' ? 'personnes regardent ce produit en ce moment' :
                      product.visitor_counter_type === 'purchases' ? 'achats dans la dernière heure' :
                      product.visitor_counter_type === 'views' ? 'vues aujourd\'hui' :
                      'ajouts au panier récents'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* UPSELL TAB */}
        <TabsContent value="upsell">
          <div className="space-y-5">
            <div className="bg-white rounded-xl border p-5 space-y-4">
              <h3 className="font-semibold text-slate-900">Ventes additionnelles (Upsell)</h3>
              <p className="text-sm text-slate-500">Produits suggérés pour remplacer ou compléter l'achat</p>
              <Select onValueChange={v => { if (v && !product.upsell_product_ids.includes(v)) update('upsell_product_ids', [...product.upsell_product_ids, v]); }}>
                <SelectTrigger><SelectValue placeholder="Ajouter un produit..." /></SelectTrigger>
                <SelectContent>
                  {allProducts.filter(p => p.id !== productId && !product.upsell_product_ids.includes(p.id)).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {product.upsell_product_ids.map((id, i) => {
                const p = allProducts.find(x => x.id === id);
                return p ? (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm">{p.name} — {p.price?.toFixed(2)} EUR</span>
                    <Button variant="ghost" size="icon" onClick={() => update('upsell_product_ids', product.upsell_product_ids.filter((_, j) => j !== i))}>
                      <Trash size={14} className="text-red-500" />
                    </Button>
                  </div>
                ) : null;
              })}
            </div>

            <div className="bg-white rounded-xl border p-5 space-y-4">
              <h3 className="font-semibold text-slate-900">Ventes croisées (Cross-sell)</h3>
              <p className="text-sm text-slate-500">Produits complémentaires proposés dans le panier</p>
              <Select onValueChange={v => { if (v && !product.crosssell_product_ids.includes(v)) update('crosssell_product_ids', [...product.crosssell_product_ids, v]); }}>
                <SelectTrigger><SelectValue placeholder="Ajouter un produit..." /></SelectTrigger>
                <SelectContent>
                  {allProducts.filter(p => p.id !== productId && !product.crosssell_product_ids.includes(p.id)).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {product.crosssell_product_ids.map((id, i) => {
                const p = allProducts.find(x => x.id === id);
                return p ? (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm">{p.name} — {p.price?.toFixed(2)} EUR</span>
                    <Button variant="ghost" size="icon" onClick={() => update('crosssell_product_ids', product.crosssell_product_ids.filter((_, j) => j !== i))}>
                      <Trash size={14} className="text-red-500" />
                    </Button>
                  </div>
                ) : null;
              })}
            </div>

            <div className="bg-white rounded-xl border p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Vente en pack</h3>
                  <p className="text-sm text-slate-500">Proposer ce produit dans un lot avec remise</p>
                </div>
                <Switch checked={product.is_bundle} onCheckedChange={v => update('is_bundle', v)} />
              </div>
              {product.is_bundle && (
                <div className="space-y-3 pt-3 border-t">
                  <div>
                    <Label>Réduction du pack (%)</Label>
                    <Input type="number" step="1" value={product.bundle_discount_percent || ''} onChange={e => update('bundle_discount_percent', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Ex: 15" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* SEO TAB */}
        <TabsContent value="seo">
          <div className="bg-white rounded-xl border p-5 space-y-4">
            <h3 className="font-semibold text-slate-900">Référencement SEO</h3>
            <div>
              <Label>Meta titre</Label>
              <Input value={product.meta_title || ''} onChange={e => update('meta_title', e.target.value)} placeholder="Titre pour les moteurs de recherche" />
              <p className="text-xs text-slate-500 mt-1">{(product.meta_title || '').length}/60 caractères</p>
            </div>
            <div>
              <Label>Meta description</Label>
              <textarea className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500" value={product.meta_description || ''} onChange={e => update('meta_description', e.target.value)} placeholder="Description pour les résultats Google" />
              <p className="text-xs text-slate-500 mt-1">{(product.meta_description || '').length}/160 caractères</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg mt-4">
              <p className="text-xs text-slate-500 mb-1">Apercu Google</p>
              <p className="text-blue-600 text-base font-medium">{product.meta_title || product.name || 'Titre du produit'}</p>
              <p className="text-emerald-700 text-xs">{`ma-boutique.shopizy.app/produit/${product.slug || 'mon-produit'}`}</p>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{product.meta_description || product.short_description || 'Description du produit...'}</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductFormPage;
